import { css } from '@compiled/react';

import { wacToken } from '@/libraries/design/foundations';

import { tableBorderNotLastCellStyles, tableCellStyles } from './shared-styles.ts';
import { TableRow, type TableRowProps } from './table-row';

const TABLE_HEADERS = ['Work', 'Status', 'Due date', 'Priority', 'Assignee'];

const tableContainerCss = css({
	width: '72.875rem',
	maxWidth: '100%',
	margin: '0 auto',
	'@media (max-width: 1023px)': {
		overflowX: 'scroll',
	},
});

const tableCss = css({
	tableLayout: 'auto',
	borderRadius: '0.4rem',
	borderCollapse: 'separate',
	borderSpacing: 0,
	// eslint-disable-next-line local/ensure-wac-token-usage
	borderColor: 'rgba(11, 18, 40, 0.14)',
	borderWidth: '1px',
	borderStyle: 'solid',
	width: '100%',
	background: wacToken('color.neutral.light0'),
	fontFamily: 'var(--atlassian-sans-font-family)',
});

const tableCellHeaderCss = css({
	backgroundColor: wacToken('color.neutral.light100'),
	fontWeight: 700,
	fontSize: '0.688rem',
	color: wacToken('color.neutral.light800'),
	// eslint-disable-next-line local/ensure-wac-token-usage
	borderBlockEndColor: 'rgba(11, 18, 40, 0.14)',
	borderBlockEndStyle: 'solid',
	borderBlockEndWidth: '1px',
	textAlign: 'start',
});

export interface PreviewTableProps {
	// Allow sparse/placeholder rows for progressive rendering.
	rows?: Array<Omit<TableRowProps, 'isLastRow'> | undefined>;
	spaceName?: string;
}

export const PreviewTable = ({ rows }: PreviewTableProps) => {
	const headerCells = TABLE_HEADERS.map((columnHeader) => {
		return (
			<th
				key={`column-header-${columnHeader}`}
				css={[
					css(tableCellStyles),
					tableCellHeaderCss,
					columnHeader !== 'Assignee' && css(tableBorderNotLastCellStyles),
				]}
			>
				{columnHeader}
			</th>
		);
	});

	const defaultRowCount = 6;
	const rowCount = rows?.length || defaultRowCount;
	const rowCells = Array.from({ length: rowCount }, (_, i) => {
		const isLastRow = i === rowCount - 1;

		if (rows && rows.length > 0 && rows[i]) {
			const { type, summary, status, dueDate, priority, assignee, assigneeImgSrc } = rows[i];
			// Ensure first row defaults to epic if type is missing/undefined (only for Kanban)
			return (
				<TableRow
					type={type}
					summary={summary}
					status={status}
					dueDate={dueDate}
					priority={priority}
					assignee={assignee}
					assigneeImgSrc={assigneeImgSrc}
					isLastRow={isLastRow}
					key={'row-' + i}
				/>
			);
		}

		return <TableRow type={i === 0 ? 'epic' : 'task'} isLastRow={isLastRow} key={'row-' + i} />;
	});

	return (
		<>
			<div css={tableContainerCss}>
				<table css={tableCss}>
					<thead>
						<tr>{headerCells}</tr>
					</thead>
					<tbody>{rowCells}</tbody>
				</table>
			</div>
		</>
	);
};
